// Must stay the first import: it takes the onboarding link's token off the
// address bar before anything else (monitoring included) reads the URL.
/* eslint-disable perfectionist/sort-imports */
import './onboardingFragment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Provider } from 'react-redux';
import { createPortal } from 'react-dom';
import minMax from 'dayjs/plugin/minMax';
import isToday from 'dayjs/plugin/isToday';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import { createRoot } from 'react-dom/client';
import isBetween from 'dayjs/plugin/isBetween';
import { ToastContainer } from 'react-toastify';
import localeData from 'dayjs/plugin/localeData';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isPropValid from '@emotion/is-prop-valid';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';
import { StyleSheetManager } from 'styled-components';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { App } from 'app';
import { store } from 'store';
import { Config } from 'config';
import { LoaderRedux } from 'components';
import { interceptor } from 'api/instance';
import { ThemeWrap, ThemeContextProvider } from 'context';

import { GlobalStyles } from './styles';
import reportWebVitals from './reportWebVitals';
import { setupMonitoring } from './setupMonitoring';

dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(minMax);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(isTomorrow);
dayjs.extend(isYesterday);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);

setupMonitoring();
interceptor(store.dispatch);

if (Config.waf.apiUrl) {
  const AwsWafScript = document.createElement('script');
  AwsWafScript.id = 'AwsWAFScript';
  AwsWafScript.async = false;
  AwsWafScript.src = Config.waf.apiUrl;
  document.head.appendChild(AwsWafScript);
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <Provider store={store}>
    <StyleSheetManager
      shouldForwardProp={(propName, elementToBeRendered) =>
        typeof elementToBeRendered === 'string' ? isPropValid(propName) : true
      }
    >
      <ThemeContextProvider>
        <ThemeWrap>
          <LoaderRedux>
            <GlobalStyles />
            <App />
            {createPortal(
              <ToastContainer
                newestOnTop
                hideProgressBar
                autoClose={5000}
                closeButton={false}
                position="top-right"
              />,
              document.body
            )}
          </LoaderRedux>
        </ThemeWrap>
      </ThemeContextProvider>
    </StyleSheetManager>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
